import { SemanticVersion } from '../types/terms';

/** Version assigned to the very first terms document. */
export const INITIAL_VERSION = '1.0.0';

/**
 * Parses a version string into its numeric parts.
 *
 * Tolerant on purpose: documents created before the admin panel existed may
 * carry hand-written values like "1.0", "v2" or an empty string, and a publish
 * must never be blocked by one malformed historical doc. Anything unparseable
 * degrades to 0.0.0 so it simply sorts below real versions.
 */
export function parseVersion(version: string | null | undefined): SemanticVersion {
  const fallback: SemanticVersion = { major: 0, minor: 0, patch: 0 };
  if (!version) return fallback;

  const match = String(version)
    .trim()
    .replace(/^v/i, '')
    .match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?/);

  if (!match) return fallback;

  return {
    major: Number(match[1]),
    minor: Number(match[2] ?? 0),
    patch: Number(match[3] ?? 0),
  };
}

export function formatVersion({ major, minor, patch }: SemanticVersion): string {
  return `${major}.${minor}.${patch}`;
}

/** Returns > 0 when `a` is newer than `b`, < 0 when older, 0 when equal. */
export function compareVersions(a: string, b: string): number {
  const left = parseVersion(a);
  const right = parseVersion(b);

  return (
    left.major - right.major || left.minor - right.minor || left.patch - right.patch
  );
}

/**
 * Next patch version after `version`, e.g. "1.0.2" -> "1.0.3".
 *
 * Every publish bumps the patch, and the mobile app re-prompts every user whose
 * stored `termsAcceptedVersion` differs from the newest one, so each call here
 * means a fresh acceptance for the entire user base.
 */
export function nextPatchVersion(version: string | null | undefined): string {
  if (!version) return INITIAL_VERSION;

  const parsed = parseVersion(version);
  return formatVersion({ ...parsed, patch: parsed.patch + 1 });
}

/**
 * Highest version across every published doc.
 *
 * The bump is computed from the maximum rather than from whichever doc has the
 * newest `updatedAt`, so a backfilled or out-of-order timestamp can never hand
 * out a version string that already exists.
 */
export function highestVersion(versions: string[]): string | null {
  if (versions.length === 0) return null;

  return versions.reduce((highest, candidate) =>
    compareVersions(candidate, highest) > 0 ? candidate : highest
  );
}
